// CMS-domain Zod schemas.
//
// This file is the wire contract between CMS and its consumers (frontend,
// future external services). It contains zero Drizzle types — CMS adapters
// translate Drizzle row shapes to/from these schemas at the boundary.
//
// Surfaces append schemas here as they ship:
//   - PRD 1 (this block): product marketing list/find/update/bulk update
//   - PRD 2: itinerary CMS visibility
//   - PRD 3: marketing AI jobs (regenerate + translate)
//   - PRD 4: static pages
//   - PRD 5: hashtag taxonomy
//
// See apps/cms/prd/cms-overview.md §A6 for the rationale.

import { z } from 'zod';

// ============================================================================
// PRD 1 — Product marketing CRUD
// ============================================================================

// Multi-language text record: [{ language: 'en', text: '…' }, …]. Legacy
// uses `ProductTitleDto[]`; we keep the array shape so it round-trips 1:1
// with the legacy column contents.
export const cmsLanguageTextSchema = z.object({
  language: z.string().min(2).max(10),
  text: z.string(),
});
export type CmsLanguageText = z.infer<typeof cmsLanguageTextSchema>;

// Marketing description: per-language sections of rich text. API shape uses
// `language` + section `content`; the DB adapter maps to/from the legacy
// column shape ({ languageCode, sections: [{ title, text, imageIndices }] }).
// `imageIndices` references images in the product's attachments array.
export const cmsMarketingDescriptionSchema = z.object({
  language: z.string().min(2).max(10),
  sections: z.array(
    z.object({
      title: z.string().nullable().optional(),
      content: z.string(),
      imageIndices: z.array(z.number().int().nonnegative()).optional(),
    })
  ),
});
export type CmsMarketingDescription = z.infer<
  typeof cmsMarketingDescriptionSchema
>;

// CMS list-item projection. Mirrors the legacy `select[]` projection at
// travel_planner_back/src/cms/services/cms.service.ts:44-70. Freeform
// jsonb columns (`title`, `description`, `attachments`, `tags`,
// `locationPoint`) are `z.unknown().nullable()` — the marketing surface
// round-trips them without interpreting.
export const cmsProductListItemSchema = z.object({
  id: z.uuid(),
  title: z.unknown().nullable(),
  titleEn: z.string().nullable(),
  description: z.unknown().nullable(),
  attachments: z.unknown().nullable(),
  tags: z.unknown().nullable(),
  currency: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  locationPoint: z.unknown().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  stars: z.string().nullable(),
  pricingUnits: z.number().int().nullable(),
  notes: z.string().nullable(),
  pricingType: z.string().nullable(),
  sections: z.array(z.string()).nullable(),
  hashtags: z.array(z.string()).nullable(),
  marketingTitle: z.array(cmsLanguageTextSchema).nullable(),
  marketingDescription: z.array(cmsMarketingDescriptionSchema).nullable(),
  shortMarketingText: z.array(cmsLanguageTextSchema).nullable(),
  cmsHero: z.string().nullable(),
  isHiddenOnCms: z.boolean().nullable(),
  priority: z.number().int().nullable(),
  cmsHeroVideo: z.string().nullable(),
  // Product videos jsonb (string[] of URLs). Freeform — surfaced so the
  // regenerate flow can auto-pick the first as the hero video.
  videos: z.unknown().nullable(),
});
export type CmsProductListItem = z.infer<typeof cmsProductListItemSchema>;

export const findCmsProductsInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(500).optional(),
  title: z.string().min(1).optional(),
  // Stringly-typed for forward-compat. The Drizzle adapter coerces to the
  // enum at the storage boundary.
  contentCategory: z.string().optional(),
});
export type FindCmsProductsInput = z.infer<typeof findCmsProductsInput>;

export const findCmsProductsResultSchema = z.object({
  data: z.array(cmsProductListItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindCmsProductsResult = z.infer<typeof findCmsProductsResultSchema>;

export const updateCmsProductMarketingInput = z
  .object({
    id: z.uuid(),
    marketingTitle: z.array(cmsLanguageTextSchema).nullable().optional(),
    marketingDescription: z
      .array(cmsMarketingDescriptionSchema)
      .nullable()
      .optional(),
    shortMarketingText: z.array(cmsLanguageTextSchema).nullable().optional(),
    hashtags: z.array(z.string()).nullable().optional(),
    sections: z.array(z.string()).nullable().optional(),
    cmsHero: z.string().nullable().optional(),
    isHiddenOnCms: z.boolean().nullable().optional(),
    priority: z.number().int().min(0).max(100).nullable().optional(),
    cmsHeroVideo: z.string().nullable().optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== 'id').length > 0, {
    message: 'At least one patch field must be provided',
  });
export type UpdateCmsProductMarketingInput = z.infer<
  typeof updateCmsProductMarketingInput
>;

// Bulk update caps at 500 items — UI gesture cap, not a queue. Anything
// larger should flow through PRD 3's queue.
export const bulkUpdateCmsProductMarketingInput = z.object({
  items: z.array(updateCmsProductMarketingInput).min(1).max(500),
});
export type BulkUpdateCmsProductMarketingInput = z.infer<
  typeof bulkUpdateCmsProductMarketingInput
>;

export const bulkUpdateCmsProductMarketingResultSchema = z.object({
  success: z.boolean(),
  updatedIds: z.array(z.uuid()),
});
export type BulkUpdateCmsProductMarketingResult = z.infer<
  typeof bulkUpdateCmsProductMarketingResultSchema
>;

// ============================================================================
// PRD 2 — Itinerary CMS visibility
// ============================================================================

export const cmsItineraryVisibilitySchema = z.enum(['visible', 'hidden']);
export type CmsItineraryVisibility = z.infer<
  typeof cmsItineraryVisibilitySchema
>;

// Hashtag-AND filter currently returns no hashtags per row (PRD 5 lights
// this up). Shape kept as legacy expected so the UI doesn't need to
// branch.
export const cmsItineraryHashtagRefSchema = z.object({
  itineraryHashtagId: z.uuid(),
  hashtagId: z.uuid(),
  category: z.string().nullable(),
  name: z.string().nullable(),
});
export type CmsItineraryHashtagRef = z.infer<
  typeof cmsItineraryHashtagRefSchema
>;

export const cmsItineraryListItemSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  country: z.string().nullable(),
  isShownOnCms: z.boolean(),
  organizationId: z.uuid(),
  activeVersion: z
    .object({
      id: z.uuid(),
      accountingReference: z.string().nullable(),
    })
    .nullable(),
  hashtags: z.array(cmsItineraryHashtagRefSchema),
  hashtagCount: z.number().int().nonnegative(),
});
export type CmsItineraryListItem = z.infer<typeof cmsItineraryListItemSchema>;

export const findCmsItinerariesInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(500).optional(),
  search: z.string().min(1).optional(),
  visibility: cmsItineraryVisibilitySchema.optional(),
  // AND-combined: an itinerary matches only when it carries EVERY hashtag in
  // the list (the Drizzle reader's `HAVING COUNT(DISTINCT) = N` subquery).
  hashtagIds: z.array(z.uuid()).optional(),
});
export type FindCmsItinerariesInput = z.infer<typeof findCmsItinerariesInput>;

export const findCmsItinerariesResultSchema = z.object({
  data: z.array(cmsItineraryListItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindCmsItinerariesResult = z.infer<
  typeof findCmsItinerariesResultSchema
>;

export const updateCmsItineraryInput = z.object({
  id: z.uuid(),
  isShownOnCms: z.boolean(),
});
export type UpdateCmsItineraryInput = z.infer<typeof updateCmsItineraryInput>;

// ============================================================================
// PRD 3 — Marketing AI (single-product sync regenerate)
// ============================================================================

export const regenerateMarketingInput = z.object({
  id: z.uuid(),
});
export type RegenerateMarketingInput = z.infer<typeof regenerateMarketingInput>;

export const regenerateShortMarketingTextInput = z.object({
  id: z.uuid(),
  // Optional unsaved description from the detail page's edit banner —
  // lets the consultant regenerate the short text against in-flight
  // edits without saving first.
  marketingDescription: z
    .array(cmsMarketingDescriptionSchema)
    .nullable()
    .optional(),
});
export type RegenerateShortMarketingTextInput = z.infer<
  typeof regenerateShortMarketingTextInput
>;

// --- Async marketing-AI jobs (catalog translate + regenerate, BullMQ) ----

export const cmsMarketingTranslateStartInput = z.object({
  catalogId: z.uuid(),
  sourceLanguage: z.string().min(2).max(10),
  targetLanguages: z.array(z.string().min(2).max(10)).min(1).max(10),
  limit: z.number().int().min(1).max(10_000).optional(),
  overrideExisting: z.boolean().optional(),
});
export type CmsMarketingTranslateStartInput = z.infer<
  typeof cmsMarketingTranslateStartInput
>;

export const cmsMarketingRegenerateStartInput = z.object({
  catalogId: z.uuid(),
  mode: z.enum(['all', 'missing']),
  limit: z.number().int().min(1).max(10_000).optional(),
});
export type CmsMarketingRegenerateStartInput = z.infer<
  typeof cmsMarketingRegenerateStartInput
>;

export const cmsMarketingJobStartedSchema = z.object({
  jobId: z.string().min(1),
});
export type CmsMarketingJobStarted = z.infer<
  typeof cmsMarketingJobStartedSchema
>;

export const cmsMarketingJobStatusInput = z.object({
  jobId: z.string().min(1),
});
export type CmsMarketingJobStatusInput = z.infer<
  typeof cmsMarketingJobStatusInput
>;

export const cmsMarketingJobProgressSchema = z.object({
  total: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  failedIds: z.array(z.string()),
});
export type CmsMarketingJobProgress = z.infer<
  typeof cmsMarketingJobProgressSchema
>;

// `state` mirrors BullMQ job states (waiting/active/completed/failed/…);
// `null` status means the job id is unknown (expired/never existed).
export const cmsMarketingJobStatusSchema = z
  .object({
    state: z.string(),
    progress: cmsMarketingJobProgressSchema.nullable(),
  })
  .nullable();
export type CmsMarketingJobStatus = z.infer<typeof cmsMarketingJobStatusSchema>;

export const cmsMarketingJobCancelResultSchema = z.object({
  cancelled: z.boolean(),
});
export type CmsMarketingJobCancelResult = z.infer<
  typeof cmsMarketingJobCancelResultSchema
>;

// ============================================================================
// PRD 4 — Static pages (landing pages + org-level meta/settings)
// ============================================================================

// Block discriminator. Stringly-typed `type` so the block library can
// grow without schema migrations; future renderers register per type.
// `.catchall(z.json())` preserves legacy AIT-CMS rows that stored
// block fields flat (siblings of `type`) instead of nested under `data`.
// Both stay concrete JSON trees — server-fn boundaries reject `unknown`.
export const cmsPageBlockSchema = z
  .object({
    type: z.string().min(1),
    data: z.record(z.string(), z.json()).default({}),
  })
  .catchall(z.json());
export type CmsPageBlock = z.infer<typeof cmsPageBlockSchema>;

// Multi-language content shape: one entry per language, each carrying
// an ordered list of blocks.
export const cmsPageLanguageContentSchema = z.object({
  languageCode: z.string().min(2).max(10),
  blocks: z.array(cmsPageBlockSchema),
});
export type CmsPageLanguageContent = z.infer<
  typeof cmsPageLanguageContentSchema
>;

export const cmsPageSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  slug: z.string().min(1).max(120),
  title: z.string().min(1).max(256),
  // Freeform array of language entries. Empty array on create until the
  // editor adds blocks.
  content: z.array(cmsPageLanguageContentSchema),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});
export type CmsPage = z.infer<typeof cmsPageSchema>;

export const createCmsPageInput = z.object({
  // One optional `/segment` allows the legacy country-variant namespaces
  // (`about-us/france`, `reviews/italy`, `landing/morocco`) — those rows
  // must keep the exact slashed shape legacy + the public site read.
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(
      /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\/[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)?$/,
      {
        message:
          'slug must be lowercase alphanumeric with dashes, with at most one "/" segment',
      }
    ),
  title: z.string().min(1).max(256),
  content: z.array(cmsPageLanguageContentSchema).default([]),
});
export type CreateCmsPageInput = z.infer<typeof createCmsPageInput>;

export const updateCmsPageInput = z
  .object({
    slug: z.string().min(1).max(120),
    title: z.string().min(1).max(256).optional(),
    content: z.array(cmsPageLanguageContentSchema).optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== 'slug').length > 0, {
    message: 'At least one patch field must be provided',
  });
export type UpdateCmsPageInput = z.infer<typeof updateCmsPageInput>;

export const removeCmsPageInput = z.object({
  slug: z.string().min(1).max(120),
});
export type RemoveCmsPageInput = z.infer<typeof removeCmsPageInput>;

export const translateCmsPageInput = z.object({
  slug: z.string().min(1).max(120),
  sourceLanguage: z.string().min(2).max(10),
  targetLanguages: z.array(z.string().min(2).max(10)).min(1).max(10),
  // "Keep already translated content": leave what the target language has and
  // translate only what the source has added since — a block it never had, or
  // a new item in one it does. Default false = translate the page again, the
  // behaviour before the flag existed. (The catalog's own job expresses the
  // same idea the other way round, as `overrideExisting`.)
  keepExisting: z.boolean().optional(),
});
export type TranslateCmsPageInput = z.infer<typeof translateCmsPageInput>;

// Org-level meta and settings are not their own table: they live on the
// `meta` / `settings` JSONB columns of two dedicated `cms_page` rows,
// identified by these slugs (the v2 `CMS_META_SLUG` / `CMS_SETTINGS_SLUG`
// contract). Shared here because more than one bounded context reads them —
// the CMS repository and the public storefront's branding read — and a
// reader that guesses at "whichever row has a non-null column" picks up an
// unrelated page's values instead. That was a real v2->v3 regression; see
// the header of apps/cms/api/src/adapters/drizzle-cms-page-repository.ts.
export const CMS_META_SLUG = 'meta';
export const CMS_SETTINGS_SLUG = 'settings';

// Org-level meta/settings — freeform "catchall" shape so the marketing
// team can experiment with keys without round-tripping a code change.
export const cmsPageMetaSchema = z
  .object({
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    keywords: z.array(z.string()).nullable().optional(),
    // Open-Graph / org-identity fields (legacy Meta Info tab). Favicon +
    // orgImage are URL strings (not uploads). `.catchall` keeps any
    // not-yet-modelled field round-tripping.
    copyright: z.string().nullable().optional(),
    orgUrl: z.string().nullable().optional(),
    orgImage: z.string().nullable().optional(),
    orgSiteName: z.string().nullable().optional(),
    orgDescription: z.string().nullable().optional(),
    orgLocale: z.string().nullable().optional(),
    favicon: z.string().nullable().optional(),
  })
  .catchall(z.unknown());
export type CmsPageMeta = z.infer<typeof cmsPageMetaSchema>;

export const cmsPageSettingsSchema = z
  .object({
    // Default language for the public CMS site (legacy Settings tab).
    defaultLanguage: z.string().nullable().optional(),
    headerVariant: z.string().nullable().optional(),
    footerVariant: z.string().nullable().optional(),
    // uploadthing keys for storefront branding logos. `headerLogoKey` (ideally
    // transparent) drives the header + loading screen; `footerLogoKey` the
    // footer. Both fall back to the org logo when unset.
    headerLogoKey: z.string().nullable().optional(),
    footerLogoKey: z.string().nullable().optional(),
  })
  .catchall(z.unknown());
export type CmsPageSettings = z.infer<typeof cmsPageSettingsSchema>;

// ============================================================================
// PRD 5 — Hashtag taxonomy (categoryHashtag CRUD only — sync refactor deferred)
// ============================================================================

// Mirrors organizations.category_hashtag_category_enum on the storage
// side. Kept as a Zod enum so the wire surface stays explicit.
export const categoryHashtagCategorySchema = z.enum([
  'hotel',
  'experience',
  'restaurant',
  'landing',
]);
export type CategoryHashtagCategory = z.infer<
  typeof categoryHashtagCategorySchema
>;

export const categoryHashtagSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  category: categoryHashtagCategorySchema,
  name: z.string().min(1).max(120),
  // Stored on the row. Computed by the service from `name` — exposed on
  // the wire so consumers can match against `hashtag_association`
  // rows by normalized form without re-implementing the normalization.
  normalizedName: z.string().nullable(),
  country: z.string().nullable(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});
export type CategoryHashtag = z.infer<typeof categoryHashtagSchema>;

export const findCategoryHashtagsInput = z
  .object({
    category: categoryHashtagCategorySchema.optional(),
    country: z.string().nullable().optional(),
    // Substring match against name (case-insensitive). Adapter handles
    // the ILIKE / accent-stripping if available.
    search: z.string().min(1).optional(),
  })
  .optional();
export type FindCategoryHashtagsInput = z.infer<
  typeof findCategoryHashtagsInput
>;

export const createCategoryHashtagInput = z.object({
  category: categoryHashtagCategorySchema,
  name: z.string().min(1).max(120),
  country: z.string().nullable().optional(),
});
export type CreateCategoryHashtagInput = z.infer<
  typeof createCategoryHashtagInput
>;

export const updateCategoryHashtagInput = z
  .object({
    id: z.uuid(),
    category: categoryHashtagCategorySchema.optional(),
    name: z.string().min(1).max(120).optional(),
    country: z.string().nullable().optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== 'id').length > 0, {
    message: 'At least one patch field must be provided',
  });
export type UpdateCategoryHashtagInput = z.infer<
  typeof updateCategoryHashtagInput
>;

export const removeCategoryHashtagInput = z.object({ id: z.uuid() });
export type RemoveCategoryHashtagInput = z.infer<
  typeof removeCategoryHashtagInput
>;

// Bulk-save a single (category, country) bucket: the editor sends the full
// desired set of hashtag names for that bucket and the service reconciles —
// inserts new names, deletes removed ones, leaves unchanged names (and their
// ids, referenced by product/itinerary links) untouched. `country: null`
// targets the global (no-country) bucket.
export const saveAllCategoryHashtagsInput = z.object({
  category: categoryHashtagCategorySchema,
  country: z.string().min(1).nullable(),
  names: z.array(z.string().min(1).max(120)).max(500),
});
export type SaveAllCategoryHashtagsInput = z.infer<
  typeof saveAllCategoryHashtagsInput
>;

export const saveAllCategoryHashtagsResultSchema = z.object({
  created: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  kept: z.number().int().nonnegative(),
});
export type SaveAllCategoryHashtagsResult = z.infer<
  typeof saveAllCategoryHashtagsResultSchema
>;

// Editorial review surface — fetch `hashtag_association` rows for a
// set of source hashtags, enriched with the display name of each
// target hashtag (resolved via the matching category_hashtag row).
export const findHashtagAssociationsInput = z.object({
  hashtagIds: z.array(z.uuid()).min(1).max(100),
});
export type FindHashtagAssociationsInput = z.infer<
  typeof findHashtagAssociationsInput
>;

export const hashtagAssociationReviewTargetSchema = z.object({
  normalizedName: z.string(),
  name: z.string(),
  category: categoryHashtagCategorySchema,
  score: z.number(),
});
export type HashtagAssociationReviewTarget = z.infer<
  typeof hashtagAssociationReviewTargetSchema
>;

export const hashtagAssociationReviewItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  normalizedName: z.string().nullable(),
  category: categoryHashtagCategorySchema,
  country: z.string().nullable(),
  associations: z.array(hashtagAssociationReviewTargetSchema),
});
export type HashtagAssociationReviewItem = z.infer<
  typeof hashtagAssociationReviewItemSchema
>;

// Product ↔ category_hashtag join row. `aiScore` is set by the AI
// classifier (deferred — see PRD 5 §out-of-scope); `customScore` is
// set by the editor. The list query returns rows ordered by
// GREATEST(customScore, aiScore) DESC.
export const productHashtagSchema = z.object({
  id: z.uuid(),
  productId: z.uuid(),
  hashtagId: z.uuid(),
  aiScore: z.number().nullable(),
  customScore: z.number().nullable(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});
export type ProductHashtag = z.infer<typeof productHashtagSchema>;

// Returned by listForProduct — the join row plus the resolved
// CategoryHashtag (null if the hashtag was deleted, defensive).
export const productHashtagWithHashtagSchema = productHashtagSchema.extend({
  hashtag: categoryHashtagSchema.nullable(),
});
export type ProductHashtagWithHashtag = z.infer<
  typeof productHashtagWithHashtagSchema
>;

export const listProductHashtagsInput = z.object({
  productId: z.uuid(),
});
export type ListProductHashtagsInput = z.infer<typeof listProductHashtagsInput>;

export const createProductHashtagInput = z.object({
  productId: z.uuid(),
  hashtagId: z.uuid(),
  customScore: z.number().min(0).max(1).nullable().optional(),
});
export type CreateProductHashtagInput = z.infer<
  typeof createProductHashtagInput
>;

export const updateProductHashtagInput = z.object({
  id: z.uuid(),
  customScore: z.number().min(0).max(1).nullable().optional(),
});
export type UpdateProductHashtagInput = z.infer<
  typeof updateProductHashtagInput
>;

export const removeProductHashtagInput = z.object({ id: z.uuid() });
export type RemoveProductHashtagInput = z.infer<
  typeof removeProductHashtagInput
>;

// ----------------------------------------------------------------------------
// assign-hashtags admin job — rule-based classifier + persistence into
// product_hashtag. See apps/cms/api/src/product-hashtags/assign-service.ts.
// ----------------------------------------------------------------------------

// Product content_category values used to filter the assignForOrg
// window. Same set as the legacy ait-cms-api `Product.content_category`
// enum + the CMS port's LegacyContentCategory.
export const productContentCategorySchema = z.enum([
  'Accommodation',
  'Activity',
  'Attraction',
  'Dining',
  'Transportation',
]);
export type ProductContentCategory = z.infer<
  typeof productContentCategorySchema
>;

export const seedTaxonomyFromRulesInput = z.object({
  // ISO country names (e.g. "Portugal"). Cannot be empty — operator
  // must pick at least one country to seed.
  countries: z.array(z.string().min(1).max(120)).min(1).max(50),
});
export type SeedTaxonomyFromRulesInput = z.infer<
  typeof seedTaxonomyFromRulesInput
>;

export const seedTaxonomyFromRulesResult = z.object({
  inserted: z.number().int().min(0),
  considered: z.number().int().min(0),
});
export type SeedTaxonomyFromRulesResult = z.infer<
  typeof seedTaxonomyFromRulesResult
>;

export const assignProductHashtagsInput = z.object({
  productId: z.uuid(),
  dryRun: z.boolean().optional(),
});
export type AssignProductHashtagsInput = z.infer<
  typeof assignProductHashtagsInput
>;

export const assignProductHashtagsResult = z.object({
  productId: z.uuid(),
  matchesFound: z.number().int().min(0),
  taxonomyMissing: z.array(z.string()),
  inserted: z.number().int().min(0),
  updated: z.number().int().min(0),
  skipped: z.enum(['not-found', 'unmappable-category']).optional(),
});
export type AssignProductHashtagsResult = z.infer<
  typeof assignProductHashtagsResult
>;

export const assignOrgHashtagsInput = z.object({
  contentCategory: productContentCategorySchema.optional(),
  country: z.string().min(1).max(120).optional(),
  // Hard cap mirrors PRODUCTS_EXPORT_ROW_CAP — admin job runs against a
  // bounded window per call. Operator pages with offset.
  limit: z.number().int().min(1).max(2000).optional(),
  offset: z.number().int().min(0).optional(),
  dryRun: z.boolean().optional(),
});
export type AssignOrgHashtagsInput = z.infer<typeof assignOrgHashtagsInput>;

export const assignOrgHashtagsResult = z.object({
  processed: z.number().int().min(0),
  matched: z.number().int().min(0),
  inserted: z.number().int().min(0),
  updated: z.number().int().min(0),
  skippedCrossOrg: z.number().int().min(0),
  taxonomyMissing: z.array(
    z.object({
      hashtag: z.string(),
      productCount: z.number().int().min(0),
    })
  ),
});
export type AssignOrgHashtagsResult = z.infer<typeof assignOrgHashtagsResult>;

// Itinerary ↔ category_hashtag join. Simpler than the product side —
// no score columns, just an association row.
export const itineraryHashtagSchema = z.object({
  id: z.uuid(),
  itineraryId: z.uuid(),
  hashtagId: z.uuid(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});
export type ItineraryHashtag = z.infer<typeof itineraryHashtagSchema>;

export const itineraryHashtagWithHashtagSchema = itineraryHashtagSchema.extend({
  hashtag: categoryHashtagSchema.nullable(),
});
export type ItineraryHashtagWithHashtag = z.infer<
  typeof itineraryHashtagWithHashtagSchema
>;

export const listItineraryHashtagsInput = z.object({
  itineraryId: z.uuid(),
});
export type ListItineraryHashtagsInput = z.infer<
  typeof listItineraryHashtagsInput
>;

export const createItineraryHashtagInput = z.object({
  itineraryId: z.uuid(),
  hashtagId: z.uuid(),
});
export type CreateItineraryHashtagInput = z.infer<
  typeof createItineraryHashtagInput
>;

export const removeItineraryHashtagInput = z.object({ id: z.uuid() });
export type RemoveItineraryHashtagInput = z.infer<
  typeof removeItineraryHashtagInput
>;

// --- Marketing-job wizard extensions (catalog-translate dry-run preview +
// per-product job outcomes; legacy /cms-marketing-translate/preview and the
// status payload's translatedProducts/failedProducts lists) ----------------

export const cmsMarketingTranslatePreviewInput = z.object({
  catalogId: z.uuid(),
  sourceLanguage: z.string().min(2).max(10),
});
export type CmsMarketingTranslatePreviewInput = z.infer<
  typeof cmsMarketingTranslatePreviewInput
>;

export const cmsMarketingTranslatePreviewSchema = z.object({
  // Products in the catalog (org-scoped, not deleted).
  totalCount: z.number().int().nonnegative(),
  // Of those, products with a non-empty marketing title in sourceLanguage.
  sourceCount: z.number().int().nonnegative(),
});
export type CmsMarketingTranslatePreview = z.infer<
  typeof cmsMarketingTranslatePreviewSchema
>;

export const cmsMarketingJobProductRefSchema = z.object({
  id: z.string().min(1),
  titleEn: z.string().nullable().optional(),
});
export type CmsMarketingJobProductRef = z.infer<
  typeof cmsMarketingJobProductRefSchema
>;

// Superset of cmsMarketingJobProgressSchema — the extra fields are optional
// so progress payloads written by an older worker still validate.
export const cmsMarketingJobDetailedProgressSchema =
  cmsMarketingJobProgressSchema.extend({
    succeededProducts: z.array(cmsMarketingJobProductRefSchema).optional(),
    failedProducts: z.array(cmsMarketingJobProductRefSchema).optional(),
  });
export type CmsMarketingJobDetailedProgress = z.infer<
  typeof cmsMarketingJobDetailedProgressSchema
>;

export const cmsMarketingJobDetailedStatusSchema = z
  .object({
    state: z.string(),
    progress: cmsMarketingJobDetailedProgressSchema.nullable(),
  })
  .nullable();
export type CmsMarketingJobDetailedStatus = z.infer<
  typeof cmsMarketingJobDetailedStatusSchema
>;

// ============================================================================
// Cross-DB reverse marketing pull-back (CMS → main)
// ============================================================================

// On-demand, admin-triggered pull of CMS-authored marketing fields back into
// the main product rows. Ports the legacy `sync-cms-data` CLI command — the
// three CLI flags (--limit / --offset / --dry-run) become the mutation input.
export const cmsPullbackMarketingInput = z.object({
  limit: z.number().int().positive().max(1000).default(100),
  offset: z.number().int().nonnegative().default(0),
  dryRun: z.boolean().default(false),
});
export type CmsPullbackMarketingInput = z.infer<
  typeof cmsPullbackMarketingInput
>;

export const cmsPullbackMarketingResultSchema = z.object({
  found: z.number().int(), // CMS rows with marketing data
  matched: z.number().int(), // matched to a main product
  updated: z.number().int(), // main products written (0 when dryRun)
  skipped: z.number().int(), // matched but unchanged
  notFound: z.number().int(), // CMS rows with no main product
  dryRun: z.boolean(),
});
export type CmsPullbackMarketingResult = z.infer<
  typeof cmsPullbackMarketingResultSchema
>;

// ============================================================================
// XT-428 — Itinerary CMS-DB reconcile (main → CMS)
// ============================================================================

// On-demand, System-role rebuild of the flat CMS DB itinerary set from the
// main DB. Re-upserts every non-deleted itinerary (carrying is_shown_on_cms)
// and purges CMS rows whose main itinerary is gone/deleted — repairs drift
// left by dropped fire-and-forget sync writes. `organizationId` scopes the
// run; omit to reconcile every org.
export const cmsReconcileItinerariesInput = z.object({
  organizationId: z.uuid().optional(),
  limit: z.number().int().positive().max(5000).default(1000),
  offset: z.number().int().nonnegative().default(0),
  dryRun: z.boolean().default(false),
});
export type CmsReconcileItinerariesInput = z.infer<
  typeof cmsReconcileItinerariesInput
>;

export const cmsReconcileItinerariesResultSchema = z.object({
  scanned: z.number().int(), // main itineraries in this window
  upserted: z.number().int(), // pushed to CMS (0 when dryRun); shown + hidden
  hidden: z.number().int(), // of scanned, those with isShownOnCms=false
  purged: z.number().int(), // CMS orphan rows deleted (0 when dryRun)
  dryRun: z.boolean(),
});
export type CmsReconcileItinerariesResult = z.infer<
  typeof cmsReconcileItinerariesResultSchema
>;

export const cmsReconcileCustomRegionsInput = z.object({
  organizationId: z.uuid().optional(),
  dryRun: z.boolean().default(false),
});
export type CmsReconcileCustomRegionsInput = z.infer<
  typeof cmsReconcileCustomRegionsInput
>;

export const cmsReconcileCustomRegionsResultSchema = z.object({
  scanned: z.number().int(), // non-deleted main regions
  upserted: z.number().int(), // pushed to CMS (0 when dryRun)
  purged: z.number().int(), // live CMS regions gone from main, soft-deleted
  dryRun: z.boolean(),
});
export type CmsReconcileCustomRegionsResult = z.infer<
  typeof cmsReconcileCustomRegionsResultSchema
>;
